/**
 * ChatRoom — biseda brenda një kanali ose një mesazhi direkt.
 *
 * Freskimi bëhet me polling çdo 4 sekonda: backend-i s'ka WebSocket dhe
 * `/api/chat/poll` kthen vetëm mesazhet e ardhura pas çastit të fundit që dimë.
 * Kur skeda e shfletuesit fshihet, polling-u ndalet — s'ka pse ta pyesim
 * serverin për një bisedë që askush s'e sheh; në kthim bëhet një pyetje e
 * vetme që e mbush hendekun.
 *
 * E njëjta sjellje si te `nexuma-mobile/src/screens/ChatRoomScreen.js`, ndaj
 * dy klientët tregojnë të njëjtën bisedë me të njëjtat rregulla.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, TextField, IconButton, Avatar, AvatarGroup,
  CircularProgress, Alert, Divider, Tooltip, Skeleton, Popover, ButtonBase,
  Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  List, ListItemButton, Snackbar,
} from '@mui/material';
import SendRoundedIcon        from '@mui/icons-material/SendRounded';
import DoneRoundedIcon        from '@mui/icons-material/DoneRounded';
import ArrowBackRoundedIcon   from '@mui/icons-material/ArrowBackRounded';
import TagRoundedIcon         from '@mui/icons-material/TagRounded';
import ReplyRoundedIcon       from '@mui/icons-material/ReplyRounded';
import DeleteOutlineRounded   from '@mui/icons-material/DeleteOutlineRounded';
import AddReactionOutlined    from '@mui/icons-material/AddReactionOutlined';
import CloseRoundedIcon       from '@mui/icons-material/CloseRounded';
import MoreHorizRounded       from '@mui/icons-material/MoreHorizRounded';
import StarBorderRounded      from '@mui/icons-material/StarBorderRounded';
import StarRounded            from '@mui/icons-material/StarRounded';
import PushPinOutlined        from '@mui/icons-material/PushPinOutlined';
import PushPinRounded         from '@mui/icons-material/PushPinRounded';
import ShortcutRounded        from '@mui/icons-material/ShortcutRounded';
import ContentCopyRounded     from '@mui/icons-material/ContentCopyRounded';
import InfoOutlined           from '@mui/icons-material/InfoOutlined';
import EditOutlined           from '@mui/icons-material/EditOutlined';
import {
  chatMessages, chatSend, chatPoll, chatMarkRead, chatDelete, chatReact,
  chatStar, chatPin, chatInfo, chatEdit, chatCapabilities, chatOverview,
} from '../../services/chatApi';
import useConfirm from '../../hooks/useConfirm';

const POLL_MS  = 4000;
const MAX_BODY = 4000;   // e njëjta kufi si te chatRoutes.js

/** Reagimet e shpejta — po ato të Nexuma Mobile, që një 👍 të lexohet njësoj në të dyja. */
const QUICK_REACTIONS = ['👍', '❤️', '😄', '🎉', '🙏', '👏'];

/** Citimi mbi flluskë: një rresht, sa të kuptohet cilit mesazh i përgjigjet. */
const quote = (t) => String(t || '').replace(/\s+/g, ' ').trim().slice(0, 120);

const T = {
  brand: '#3D8FA3', ink: '#0F172A', ink700: '#334155', ink500: '#64748B',
  ink400: '#94A3B8', line: '#E2E8F0', surface: '#F8FAFC', white: '#FFFFFF',
};

const pad   = (n) => String(n).padStart(2, '0');
const clock = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DAYS = ['E diel', 'E hënë', 'E martë', 'E mërkurë', 'E enjte', 'E premte', 'E shtunë'];
function daySeparator(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest  = new Date(today.getTime() - 86400000);
  if (dayKey(iso) === dayKey(today)) return 'Sot';
  if (dayKey(iso) === dayKey(yest))  return 'Dje';
  return `${DAYS[d.getDay()]}, ${d.getDate()}.${pad(d.getMonth() + 1)}`;
}

const initial = (s) => String(s || '?').trim().charAt(0).toLocaleUpperCase('sq');

/**
 * Citimi i mesazhit të cilit i përgjigjemi.
 * Serveri e dërgon si `{ id, name, body }` te fusha `reply` (chatRoutes.js).
 */
function Quoted({ reply, mine }) {
  if (!reply) return null;
  return (
    <Box sx={{
      mb: 0.75, pl: 1, py: 0.25,
      borderLeft: `3px solid ${mine ? 'rgba(255,255,255,0.6)' : T.brand}`,
    }}>
      <Typography sx={{
        fontSize: '0.7rem', fontWeight: 800, lineHeight: 1.4,
        color: mine ? 'rgba(255,255,255,0.95)' : T.brand,
      }}>
        {reply.name || 'Përdorues'}
      </Typography>
      <Typography sx={{
        fontSize: '0.75rem', lineHeight: 1.4,
        color: mine ? 'rgba(255,255,255,0.8)' : T.ink500,
      }}>
        {quote(reply.body)}
      </Typography>
    </Box>
  );
}

/** Reagimet nën flluskë. `mine` te secili element vjen nga serveri. */
function Reactions({ list, align, onToggle }) {
  if (!list?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap"
      sx={{ mt: 0.5, justifyContent: align, rowGap: 0.5 }}>
      {list.map((r) => (
        <ButtonBase
          key={r.emoji}
          onClick={() => onToggle(r.emoji)}
          aria-label={`${r.emoji} ${r.count}`}
          sx={{
            px: 0.75, py: 0.125, borderRadius: '10px', gap: 0.375,
            fontSize: '0.75rem', lineHeight: 1.6,
            bgcolor: r.mine ? 'rgba(61,143,163,0.12)' : T.surface,
            border: `1px solid ${r.mine ? T.brand : T.line}`,
            color: r.mine ? T.brand : T.ink500,
            fontWeight: r.mine ? 800 : 600,
            '&:hover': { bgcolor: 'rgba(61,143,163,0.18)' },
          }}
        >
          <span>{r.emoji}</span>
          <span>{r.count}</span>
        </ButtonBase>
      ))}
    </Stack>
  );
}

/**
 * Veprimet mbi një mesazh — dalin vetëm kur miu është mbi rreshtin.
 * Të fshehura me `opacity` e jo me `display`, që flluska të mos kërcejë.
 */
function Actions({ onReact, onReply, onMore }) {
  const btn = { width: 26, height: 26, color: T.ink400, '&:hover': { color: T.brand, bgcolor: T.surface } };
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" className="nx-msg-actions"
      sx={{ opacity: 0, transition: 'opacity 120ms', flexShrink: 0, alignSelf: 'center' }}>
      <Tooltip title="Reago" arrow>
        <IconButton size="small" sx={btn} onClick={onReact} aria-label="Reago">
          <AddReactionOutlined sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Përgjigju" arrow>
        <IconButton size="small" sx={btn} onClick={onReply} aria-label="Përgjigju">
          <ReplyRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      {/* Fshirja u zhvendos brenda menysë: rrinte krah reagimit, pra veprimi më
          i pakthyeshëm ishte edhe më i lehti për t'u prekur gabimisht. */}
      <Tooltip title="Më shumë" arrow>
        <IconButton size="small" sx={btn} onClick={onMore} aria-label="Veprime mbi mesazhin">
          <MoreHorizRounded sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/** Shenjat mbi flluskë: ylli im dhe gjilpëra e përbashkët. */
function Marks({ m, align }) {
  if (!m.starred && !m.pinned && !m.edited) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center"
      sx={{ justifyContent: align, mt: 0.25 }}>
      {m.pinned  && <PushPinRounded sx={{ fontSize: 12, color: T.ink400 }} />}
      {m.starred && <StarRounded    sx={{ fontSize: 13, color: '#CB8B37' }} />}
      {m.edited  && (
        <Typography sx={{ fontSize: '0.65rem', color: T.ink400 }}>redaktuar</Typography>
      )}
    </Stack>
  );
}

/** Rreshti i mbahet pezull veprimeve: `:hover` mbi të zbulon `Actions`. */
const HOVER = { '&:hover .nx-msg-actions': { opacity: 1 } };


/** Ngjyra e avatarit nga emri — e njëjta fytyrë sa herë që flet i njëjti person. */
const AVATAR_COLORS = ['#3D8FA3', '#6B5AA6', '#3D8F6F', '#B4713D', '#8A5A72', '#4F6BA6'];
const avatarColor = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

export default function ChatRoom({ channel, onBack, onActivity }) {
  const [messages, setMessages] = useState([]);
  const [info,     setInfo]     = useState(null);   // { name, member_count, members, purpose }
  const [draft,    setDraft]    = useState('');
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState('');
  const [replyTo,  setReplyTo]  = useState(null);   // mesazhi që po i përgjigjemi
  const [picker,   setPicker]   = useState(null);   // { anchor, message } për reagimet
  const [menu,     setMenu]     = useState(null);   // { anchor, message } për veprimet
  const [editing,  setEditing]  = useState(null);   // mesazhi që po redaktohet
  const [infoFor,  setInfoFor]  = useState(null);   // { message, data|null }
  const [fwdFor,   setFwdFor]   = useState(null);   // mesazhi që po përcillet
  const [targets,  setTargets]  = useState([]);     // bisedat ku mund të përcillet
  const [note,     setNote]     = useState('');     // konfirmim i shkurtër
  /**
   * Çfarë mbështet serveri. Pa këtë, një instalim i papërditësuar do të
   * nxirrte veprime që kthejnë 404 sapo të preken.
   */
  const [caps,     setCaps]     = useState({});

  const bottomRef = useRef(null);
  const since     = useRef(new Date(Date.now() - 1000).toISOString());
  // Mbahet te një ref që intervali i polling-ut të mos rindërtohet sa herë
  // prindi rirendohet — përndryshe çdo rirendim do ta rikthente numërimin.
  const activity  = useRef(onActivity);
  activity.current = onActivity;

  /**
   * Bashkim sipas `id`, jo shtim në fund.
   *
   * Që kur mesazhet mund të redaktohen dhe fshihen, `/poll` kthen edhe mesazhe
   * të vjetra që kanë ndryshuar: të redaktuarat me trupin e ri, të fshirat me
   * `deleted`. Një filtër që hidhte poshtë çdo id të njohur do ta linte
   * redaktimin të padukshëm, dhe një mesazh i fshirë para se ta shihnim këtu
   * do të shfaqej si flluskë bosh.
   */
  const merge = useCallback((incoming) => {
    if (!incoming?.length) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      let changed = false;
      for (const m of incoming) {
        if (m.deleted) {
          if (byId.delete(m.id)) changed = true;
          continue;
        }
        const old = byId.get(m.id);
        const reactionsChanged =
          JSON.stringify(old?.reactions || []) !== JSON.stringify(m.reactions || []);
        if (!old || old.body !== m.body || old.edited !== m.edited || reactionsChanged) {
          byId.set(m.id, { ...old, ...m });
          changed = true;
        }
      }
      if (!changed) return prev;
      return [...byId.values()].sort((a, b) => a.id - b.id);
    });
  }, []);

  /* ── Historiku ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setError('');
    (async () => {
      try {
        const data = await chatMessages(channel.id);
        if (cancelled) return;
        setMessages(data.messages);
        setInfo(data.channel || null);
        const last = data.messages[data.messages.length - 1];
        since.current = last ? last.at : new Date(Date.now() - 1000).toISOString();
        await chatMarkRead(channel.id);
        activity.current?.();
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [channel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Polling ── */
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;
      try {
        const data = await chatPoll(since.current, channel.id);
        if (stopped) return;
        since.current = data.now;
        if (data.messages.length) {
          merge(data.messages);
          await chatMarkRead(channel.id);
          activity.current?.();
        }
      } catch { /* rrjeti bie e ngrihet — prova tjetër vjen vetë */ }
    };
    const id = setInterval(tick, POLL_MS);
    // Kthimi te skeda nuk duhet të presë ciklin tjetër.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [channel.id, merge]);

  /* Rreshti i fundit mbetet në pamje — bisedat lexohen nga fundi. */
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, loading]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const m = await chatSend(channel.id, body, replyTo?.id || null);
      setDraft('');
      setReplyTo(null);
      merge([m]);
      since.current = m.at;
      activity.current?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  /**
   * Reago / hiq reagimin.
   *
   * Serveri e kthen gjendjen e re të plotë të mesazhit, kështu që gjendja lokale
   * nuk e merr me mend numërimin: e zëvendëson me atë që tha serveri. Kjo mban
   * të saktë rastin kur dy veta reagojnë brenda të njëjtit cikël polling-u.
   */
  const react = async (message, emoji) => {
    setPicker(null);
    try {
      const updated = await chatReact(channel.id, message.id, emoji);
      merge([updated && updated.id ? updated : { ...message, reactions: updated?.reactions || [] }]);
    } catch (e) {
      setError(e.message);
    }
  };

  // Çfarë lejon serveri — një herë, kur hapet dhoma.
  useEffect(() => {
    let alive = true;
    chatCapabilities().then((c) => { if (alive) setCaps(c || {}); });
    return () => { alive = false; };
  }, []);

  const close = () => setMenu(null);

  const toggleStar = async (m) => {
    close();
    try {
      const starred = await chatStar(channel.id, m.id);
      merge([{ ...m, starred: typeof starred === 'boolean' ? starred : !m.starred }]);
      setNote(starred === false ? 'Ylli u hoq' : 'U ruajt me yll');
    } catch (e) { setError(e.message); }
  };

  const togglePin = async (m) => {
    close();
    try {
      const pinned = await chatPin(channel.id, m.id);
      merge([{ ...m, pinned: typeof pinned === 'boolean' ? pinned : !m.pinned }]);
      setNote(pinned === false ? 'U hoq nga gjilpëra' : 'U gjilpërua');
    } catch (e) { setError(e.message); }
  };

  const openInfo = async (m) => {
    close();
    setInfoFor({ message: m, data: null });
    try { setInfoFor({ message: m, data: await chatInfo(channel.id, m.id) }); }
    catch (e) { setInfoFor(null); setError(e.message); }
  };

  const copy = async (m) => {
    close();
    try {
      await navigator.clipboard.writeText(m.body || '');
      setNote('U kopjua');
    } catch {
      // Shfletuesit e bllokojnë kopjimin pa veprim të drejtpërdrejtë; mos hesht.
      setError('Shfletuesi nuk lejoi kopjimin.');
    }
  };

  /** Përcjellja është dërgim i ri diku tjetër — serveri s'ka rrugë të vetën. */
  const openForward = async (m) => {
    close();
    setFwdFor(m);
    try {
      const o = await chatOverview();
      setTargets([...(o.channels || []), ...(o.dms || [])].filter((c) => c.id !== channel.id));
    } catch (e) { setError(e.message); }
  };

  const forwardTo = async (target) => {
    const m = fwdFor;
    setFwdFor(null);
    try {
      await chatSend(target.id, m.body);
      setNote(`U përcoll te ${target.kind === 'dm' ? target.name : `#${target.name}`}`);
    } catch (e) { setError(e.message); }
  };

  const startEdit = (m) => { close(); setEditing(m); setDraft(m.body || ''); setReplyTo(null); };
  const cancelEdit = () => { setEditing(null); setDraft(''); };

  const saveEdit = async () => {
    const body = draft.trim();
    if (!body || !editing) return;
    setSending(true);
    try {
      const updated = await chatEdit(channel.id, editing.id, body);
      merge([updated?.id ? updated : { ...editing, body, edited: true }]);
      cancelEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  /** Fshirja largon nga lista menjëherë; polling-u te shokët e bisedës e kap vetë. */
  const [confirm, confirmUI] = useConfirm();

  const remove = async (message) => {
    if (!(await confirm({ title: 'Fshi mesazhin', message: 'Ta fshij këtë mesazh?', confirmLabel: 'Fshi' }))) return;
    try {
      await chatDelete(channel.id, message.id);
      merge([{ id: message.id, deleted: true }]);
      if (replyTo?.id === message.id) setReplyTo(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const onKeyDown = (e) => {
    // Enter dërgon; Shift+Enter lë rresht të ri — konventa e çdo bisede.
    // Gjatë redaktimit Enter-i ruan: përndryshe teksti i ndrequr do të nisej si
    // mesazh i ri dhe origjinali do të mbetej i gabuar.
    if (e.key === 'Escape' && editing) { e.preventDefault(); cancelEdit(); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); (editing ? saveEdit : send)(); }
  };

  const title = info?.kind === 'dm' || channel.kind === 'dm'
    ? (channel.name || info?.name || 'Bisedë')
    : (info?.name || channel.name || '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, bgcolor: T.white }}>
      {/* ── Koka ── */}
      <Box sx={{
        px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: `1px solid ${T.line}`, bgcolor: T.white, flexShrink: 0,
      }}>
        {onBack && (
          <IconButton size="small" onClick={onBack} aria-label="Kthehu te lista"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Avatar sx={{
          width: 34, height: 34, fontSize: '0.85rem', fontWeight: 800,
          bgcolor: channel.kind === 'dm' ? avatarColor(title) : '#E8F4F6',
          color: channel.kind === 'dm' ? '#FFFFFF' : T.brand,
        }}>
          {channel.kind === 'dm' ? initial(title) : <TagRoundedIcon sx={{ fontSize: 19 }} />}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 800, color: T.ink, lineHeight: 1.2 }}>
            {channel.kind === 'dm' ? title : `#${title}`}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.72rem', color: T.ink500 }}>
            {channel.kind === 'dm'
              ? (channel.peer?.online ? 'Online' : 'Mesazh direkt')
              : info?.member_count
                ? `${info.member_count} ${info.member_count === 1 ? 'anëtar' : 'anëtarë'}${info.purpose ? ` · ${info.purpose}` : ''}`
                : (info?.purpose || '')}
          </Typography>
        </Box>

        {/* Fytyrat e anëtarëve — sa kush është brenda, pa hapur asgjë. */}
        {channel.kind !== 'dm' && info?.members?.length > 0 && (
          <AvatarGroup max={4} sx={{
            '& .MuiAvatar-root': { width: 26, height: 26, fontSize: '0.65rem', fontWeight: 700, borderColor: T.white },
          }}>
            {info.members.map((m) => (
              <Tooltip key={m.id} title={m.name}>
                <Avatar sx={{ bgcolor: avatarColor(m.name), color: '#FFFFFF' }}>{initial(m.name)}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        )}
      </Box>

      {/* ── Mesazhet ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, bgcolor: T.surface }}>
        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width={120} height={14} />
                  <Skeleton width={`${55 + i * 12}%`} height={34} sx={{ borderRadius: 2 }} />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : messages.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', color: T.ink400, textAlign: 'center' }}>
              Ende asnjë mesazh këtu. Shkruaj i pari.
            </Typography>
          </Box>
        ) : messages.map((m, i) => {
          const prev = messages[i - 1];
          const newDay = !prev || dayKey(prev.at) !== dayKey(m.at);
          // Mesazhet e njëpasnjëshme të të njëjtit autor nuk e përsërisin emrin.
          const grouped = prev && !newDay && prev.author?.id === m.author?.id && prev.mine === m.mine;

          return (
            <React.Fragment key={m.id}>
              {newDay && (
                <Divider sx={{
                  my: 2.5,
                  '&::before, &::after': { borderColor: T.line },
                  '& .MuiDivider-wrapper': { px: 1.5 },
                }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: T.ink500 }}>
                    {daySeparator(m.at)}
                  </Typography>
                </Divider>
              )}

              {m.mine ? (
                <Stack direction="row" spacing={0.75} alignItems="flex-end" justifyContent="flex-end"
                  sx={{ mt: grouped ? 0.5 : 2, ...HOVER }}>
                  <Actions
                    onReact={(e) => setPicker({ anchor: e.currentTarget, message: m })}
                    onReply={() => setReplyTo(m)}
                    onMore={(e) => setMenu({ anchor: e.currentTarget, message: m })}
                  />
                  <Typography sx={{ fontSize: '0.68rem', color: T.ink400, mb: 0.5 }}>{clock(m.at)}</Typography>
                  {/* Një shenjë: mesazhi mbërriti te serveri. Nuk pretendon leximin
                      — për atë do të duheshin dëftesa leximi, që s'i kemi. */}
                  <DoneRoundedIcon sx={{ fontSize: 14, color: T.ink400, mb: 0.5 }} />
                  <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
                    <Box sx={{
                      px: 1.75, py: 1.125, bgcolor: T.brand, color: '#FFFFFF',
                      borderRadius: '14px', borderBottomRightRadius: '4px',
                      fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      <Quoted reply={m.reply} mine />
                      {m.body}
                    </Box>
                    <Reactions list={m.reactions} align="flex-end" onToggle={(e) => react(m, e)} />
                    <Marks m={m} align="flex-end" />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.25} alignItems="flex-start"
                  sx={{ mt: grouped ? 0.5 : 2, ...HOVER }}>
                  {grouped ? <Box sx={{ width: 32, flexShrink: 0 }} /> : (
                    <Avatar sx={{
                      width: 32, height: 32, fontSize: '0.75rem', fontWeight: 800, flexShrink: 0,
                      bgcolor: avatarColor(m.author?.name), color: '#FFFFFF',
                    }}>
                      {initial(m.author?.name)}
                    </Avatar>
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    {!grouped && (
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: T.ink }}>
                          {m.author?.name || 'Përdorues'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: T.ink400 }}>{clock(m.at)}</Typography>
                      </Stack>
                    )}
                    <Box sx={{
                      display: 'inline-block', px: 1.75, py: 1.125,
                      bgcolor: T.white, border: `1px solid ${T.line}`,
                      borderRadius: '14px', borderTopLeftRadius: grouped ? '14px' : '4px',
                      fontSize: '0.875rem', lineHeight: 1.5, color: T.ink700,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      <Quoted reply={m.reply} mine={false} />
                      {m.body}
                    </Box>
                    <Reactions list={m.reactions} align="flex-start" onToggle={(e) => react(m, e)} />
                    <Marks m={m} align="flex-start" />
                  </Box>
                  <Actions
                    onReact={(e) => setPicker({ anchor: e.currentTarget, message: m })}
                    onReply={() => setReplyTo(m)}
                    onMore={(e) => setMenu({ anchor: e.currentTarget, message: m })}
                  />
                </Stack>
              )}
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')}
          sx={{ mx: 2, mb: 1, fontSize: '0.8rem', borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Zgjedhësi i reagimeve — një grup i vogël, jo tastierë e plotë emoji-sh. */}
      <Popover
        open={Boolean(picker)}
        anchorEl={picker?.anchor || null}
        onClose={() => setPicker(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: '999px', border: `1px solid ${T.line}`, boxShadow: '0 6px 24px rgba(15,23,42,0.12)' } }}
      >
        <Stack direction="row" spacing={0.25} sx={{ px: 0.75, py: 0.5 }}>
          {QUICK_REACTIONS.map((emoji) => (
            <ButtonBase
              key={emoji}
              onClick={() => react(picker.message, emoji)}
              aria-label={`Reago me ${emoji}`}
              sx={{
                width: 34, height: 34, borderRadius: '50%', fontSize: '1.05rem',
                '&:hover': { bgcolor: T.surface, transform: 'scale(1.15)' },
                transition: 'transform 120ms',
              }}
            >
              {emoji}
            </ButtonBase>
          ))}
        </Stack>
      </Popover>

      {/* ── Menuja e veprimeve ── */}
      <Menu
        open={Boolean(menu)}
        anchorEl={menu?.anchor || null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: '12px', border: `1px solid ${T.line}`, boxShadow: '0 8px 28px rgba(15,23,42,0.14)' } }}
      >
        {caps.star && (
          <MenuItem onClick={() => toggleStar(menu.message)}>
            <ListItemIcon>{menu?.message?.starred
              ? <StarRounded sx={{ fontSize: 19, color: '#CB8B37' }} />
              : <StarBorderRounded sx={{ fontSize: 19 }} />}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
              {menu?.message?.starred ? 'Hiq yllin' : 'Ruaj me yll'}
            </ListItemText>
          </MenuItem>
        )}
        {caps.pin && (
          <MenuItem onClick={() => togglePin(menu.message)}>
            <ListItemIcon>{menu?.message?.pinned
              ? <PushPinRounded sx={{ fontSize: 19 }} />
              : <PushPinOutlined sx={{ fontSize: 19 }} />}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
              {menu?.message?.pinned ? 'Hiq nga gjilpëra' : 'Gjilpëro'}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => openForward(menu.message)}>
          <ListItemIcon><ShortcutRounded sx={{ fontSize: 19 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Përcill</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => copy(menu.message)}>
          <ListItemIcon><ContentCopyRounded sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Kopjo</ListItemText>
        </MenuItem>
        {caps.info && (
          <MenuItem onClick={() => openInfo(menu.message)}>
            <ListItemIcon><InfoOutlined sx={{ fontSize: 19 }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Info</ListItemText>
          </MenuItem>
        )}
        {caps.edit && menu?.message?.mine && (
          <MenuItem onClick={() => startEdit(menu.message)}>
            <ListItemIcon><EditOutlined sx={{ fontSize: 19 }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Redakto</ListItemText>
          </MenuItem>
        )}
        {menu?.message?.mine && (
          <MenuItem onClick={() => { close(); remove(menu.message); }} sx={{ color: '#DC2626' }}>
            <ListItemIcon><DeleteOutlineRounded sx={{ fontSize: 19, color: '#DC2626' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Fshi</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* ── Info: kush e ka parë ── */}
      <Dialog open={Boolean(infoFor)} onClose={() => setInfoFor(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 800 }}>Info</DialogTitle>
        <DialogContent>
          {!infoFor?.data ? (
            <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={22} /></Stack>
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <Typography sx={{ fontSize: '0.82rem', color: T.ink500 }}>Dërguar</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: T.ink }}>{clock(infoFor.data.at)}</Typography>
              </Stack>
              {infoFor.data.edited_at && (
                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: T.ink500 }}>Redaktuar</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: T.ink }}>{clock(infoFor.data.edited_at)}</Typography>
                </Stack>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: T.ink500, mb: 0.5 }}>
                PARË NGA
              </Typography>
              {(infoFor.data.seen || []).length === 0 ? (
                <Typography sx={{ fontSize: '0.82rem', color: T.ink400 }}>Ende askush.</Typography>
              ) : (infoFor.data.seen || []).map((v) => (
                <Stack key={v.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: T.ink }}>{v.name}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: T.ink400 }}>{clock(v.at)}</Typography>
                </Stack>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Përcille te një bisedë tjetër ── */}
      <Dialog open={Boolean(fwdFor)} onClose={() => setFwdFor(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 800 }}>Përcille te…</DialogTitle>
        <DialogContent sx={{ px: 1 }}>
          {targets.length === 0 ? (
            <Typography sx={{ fontSize: '0.85rem', color: T.ink400, px: 2, py: 2 }}>
              S’ka bisedë tjetër ku ta përcjellësh.
            </Typography>
          ) : (
            <List dense>
              {targets.map((t) => (
                <ListItemButton key={`${t.kind}-${t.id}`} onClick={() => forwardTo(t)} sx={{ borderRadius: '10px' }}>
                  <ListItemIcon sx={{ minWidth: 38 }}>
                    <Avatar sx={{
                      width: 28, height: 28, fontSize: '0.7rem', fontWeight: 800,
                      bgcolor: t.kind === 'dm' ? avatarColor(t.name) : '#E8F4F6',
                      color: t.kind === 'dm' ? '#FFFFFF' : T.brand,
                    }}>
                      {t.kind === 'dm' ? initial(t.name) : <TagRoundedIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                    {t.kind === 'dm' ? t.name : `#${t.name}`}
                  </ListItemText>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {confirmUI}

      <Snackbar
        open={Boolean(note)}
        autoHideDuration={2200}
        onClose={() => setNote('')}
        message={note}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* ── Shkrimi ── */}
      <Box sx={{ borderTop: `1px solid ${T.line}`, bgcolor: T.white, flexShrink: 0 }}>
      {/* Redaktimi zë vendin e përgjigjes: të dyja ndryshojnë çfarë bën Enter-i,
          dhe dy shirita njëherësh do të linin të paqartë se cila është në fuqi. */}
      {editing && (
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{
            mx: 2, mt: 1.25, px: 1.25, py: 0.75, borderRadius: '10px',
            bgcolor: T.surface, borderLeft: '3px solid #CB8B37',
          }}>
          <EditOutlined sx={{ fontSize: 16, color: '#CB8B37', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#CB8B37' }}>
              Po redakton mesazhin
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.75rem', color: T.ink500 }}>
              Enter ruan · Esc anulon
            </Typography>
          </Box>
          <IconButton size="small" onClick={cancelEdit} aria-label="Anulo redaktimin"
            sx={{ color: T.ink400, '&:hover': { color: T.ink } }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      )}
      {replyTo && !editing && (
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{
            mx: 2, mt: 1.25, px: 1.25, py: 0.75, borderRadius: '10px',
            bgcolor: T.surface, borderLeft: `3px solid ${T.brand}`,
          }}>
          <ReplyRoundedIcon sx={{ fontSize: 16, color: T.brand, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: T.brand }}>
              Përgjigje për {replyTo.mine ? 'veten' : (replyTo.author?.name || 'Përdorues')}
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.75rem', color: T.ink500 }}>
              {quote(replyTo.body)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)} aria-label="Anulo përgjigjen"
            sx={{ color: T.ink400, '&:hover': { color: T.ink } }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      )}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          fullWidth multiline maxRows={6} size="small"
          placeholder={editing
            ? 'Ndrysho mesazhin…  (Enter ruan, Esc anulon)'
            : 'Shkruaj një mesazh…  (Enter dërgon, Shift+Enter rresht i ri)'}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_BODY))}
          onKeyDown={onKeyDown}
          disabled={sending}
          inputProps={{ 'aria-label': 'Shkruaj një mesazh' }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px', bgcolor: T.surface, fontSize: '0.875rem',
              '& fieldset': { borderColor: T.line },
              '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
            },
          }}
        />
        <IconButton
          onClick={editing ? saveEdit : send}
          disabled={!draft.trim() || sending}
          aria-label={editing ? 'Ruaj ndryshimin' : 'Dërgo'}
          sx={{
            width: 40, height: 40, flexShrink: 0, color: '#FFFFFF', bgcolor: T.brand,
            '&:hover': { bgcolor: '#33788A' },
            '&.Mui-disabled': { bgcolor: T.line, color: T.ink400 },
          }}
        >
          {sending
            ? <CircularProgress size={18} color="inherit" />
            : editing ? <DoneRoundedIcon sx={{ fontSize: 20 }} /> : <SendRoundedIcon sx={{ fontSize: 19 }} />}
        </IconButton>
      </Box>
      </Box>
    </Box>
  );
}
